import { useEffect, useMemo, useState } from 'react';
import type { BillingPlan } from '../types';

export type PlanColumnSlot = 'left' | 'right';

export interface PlanColumn {
  plan: BillingPlan;
  planIndex: number;
  columnKey: string;
  columnSlot?: PlanColumnSlot;
}

export function usePricingPlanColumns(
  isCompact: boolean,
  validBillingPlans: BillingPlan[],
) {
  const [leftPlanIndex, setLeftPlanIndex] = useState(0);
  const [rightPlanIndex, setRightPlanIndex] = useState(1);

  const planCount = validBillingPlans.length;

  useEffect(() => {
    if (leftPlanIndex >= planCount) {
      setLeftPlanIndex(0);
    }
    if (rightPlanIndex >= planCount) {
      setRightPlanIndex(Math.min(1, planCount - 1));
    }
  }, [planCount, leftPlanIndex, rightPlanIndex]);

  const planColumns = useMemo((): PlanColumn[] => {
    if (!isCompact) {
      return validBillingPlans.map((plan, planIndex) => ({
        plan,
        planIndex,
        columnKey: plan.plan_id,
      }));
    }

    const columns: PlanColumn[] = [];
    const leftPlan = validBillingPlans[leftPlanIndex];

    if (leftPlan) {
      columns.push({
        plan: leftPlan,
        planIndex: leftPlanIndex,
        columnKey: `left-${leftPlan.plan_id}`,
        columnSlot: 'left',
      });
    }

    const rightPlan = validBillingPlans[rightPlanIndex];

    if (rightPlan) {
      columns.push({
        plan: rightPlan,
        planIndex: rightPlanIndex,
        columnKey: `right-${rightPlan.plan_id}`,
        columnSlot: 'right',
      });
    }

    return columns;
  }, [isCompact, leftPlanIndex, rightPlanIndex, validBillingPlans]);

  return {
    planColumns,
    planCount,
    leftPlanIndex,
    rightPlanIndex,
    setLeftPlanIndex,
    setRightPlanIndex,
  };
}
